import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import { types as rankTypes } from '../../../redux/reducers/rank/types';
import { useTranslation } from 'react-i18next';
import { CreateRankDTO } from '../../../redux/reducers/api-types';

export interface UseRankError {
    validateAndSubmit: (dto: CreateRankDTO, submitFn: (dto: CreateRankDTO) => void) => void;
    clearLocalError: () => void;
    errorPayload: ErrorPayload | undefined;
}

export const useRankError = (): UseRankError => {
    const { t } = useTranslation(['rank', 'common']);
    const [localError, setLocalErrorState] = useState<ErrorPayload | undefined>(undefined);
    const rankError = useSelector((state: any) => errorSelectors.cudRankError(state));

    // Mapper for API error
    const mapRankError = useCallback(
        (error: any): ErrorPayload | undefined => {
            if (!error) return undefined;
            if (error.errorType === rankTypes.CREATE_RANK) {
                switch (error.error?.type || error.error) {
                    case 'DuplicateEntry':
                        return {
                            errorType: 'server',
                            error: '',
                            error_description: t('rank:errors.duplicate_entry'),
                            details: error.details ?? {},
                        };
                    case 'UnprocessableEntity':
                        return {
                            errorType: 'server',
                            error: '',
                            error_description: t('rank:errors.unprocessable_entity'),
                            details: error.details ?? {},
                        };
                    default:
                        return {
                            errorType: 'server',
                            error: '',
                            error_description: t('rank:errors.unknown_error'),
                            details: error.details ?? {},
                        };
                }
            }
            return error;
        },
        [t],
    );

    // Validation logic for CreateRankDTO
    const validateAndSubmit = useCallback(
        (dto: CreateRankDTO, submitFn: (dto: CreateRankDTO) => void) => {
            if (!dto.dispatch_groups || dto.dispatch_groups.length === 0) {
                setLocalErrorState({
                    errorType: 'local',
                    error: '',
                    error_description: t('rank:errors.no_dispatch_group'),
                    details: {},
                });
                return;
            }
            if (!dto.area?.polygon || dto.area.polygon.length < 3) {
                setLocalErrorState({
                    errorType: 'local',
                    error: '',
                    error_description: t('rank:errors.polygon_too_small'),
                    details: {},
                });
                return;
            }
            setLocalErrorState(undefined);
            submitFn(dto);
        },
        [t],
    );

    const clearLocalError = useCallback(() => {
        setLocalErrorState(undefined);
    }, []);

    // Priority: local error > API error
    const errorPayload: ErrorPayload | undefined = localError || mapRankError(rankError);

    return {
        validateAndSubmit,
        clearLocalError,
        errorPayload,
    };
};
